<script setup lang="ts">
import { nextTick, onMounted, ref } from 'vue';
import { posStore } from '../../../../stores/posStore';
import SearchSelect from '../../../../components/ui/form/SearchSelect.vue';
import { tokenManager } from '../../../../api/utils/tokenManager';
import { SalesChannel } from '../../../../types/models';

const pos = posStore()
const saveAsDefault = ref(false);
const expanded = ref(false);

const sales_channels_id = ref()
const warehouses_id = ref()
const price_lists_id = ref();

const warehouse = ref<any>(null);
const price_list = ref<any>(null);
const sales_channel = ref<any>(null);

function selectWarehouse(whouse: any) {
  if (pos.selectedPosInvoice && whouse) {
    warehouse.value = whouse;
    pos.selectedPosInvoice.invoice.warehouses_id = whouse.id;
    pos.selectedPosInvoice.invoice.warehouse = whouse.name;
    updateDefaults();
  }
}

function selectSalesChannel(channel: SalesChannel) {
  if (pos.selectedPosInvoice && channel) {
    sales_channel.value = channel;
    pos.selectedPosInvoice.invoice.sales_channels_id = channel.id;
    pos.selectedPosInvoice.invoice.sales_channel = channel.name;
    if (channel.require_delivery) {
      pos.selectedPosInvoice.invoice.to_deliver = true;
    } 
    updateDefaults();
  }
}

function selectPriceList(priceList: any) {
  if (pos.selectedPosInvoice && priceList) {
    price_list.value = priceList;
    pos.selectedPosInvoice.invoice.price_lists_id = priceList.id;
    pos.selectedPosInvoice.invoice.price_list = priceList.name;
    updateDefaults();
  }
}

const updateDefaults = () => {
  if (saveAsDefault.value) {
    tokenManager.setWarehouse(warehouse.value);
    tokenManager.setPriceList(price_list.value);
    tokenManager.setSalesChannel(sales_channel.value);
  }
};

onMounted(async () => {
  warehouse.value = tokenManager.getWarehouse();
    price_list.value = tokenManager.getPriceList();
    sales_channel.value = tokenManager.getSalesChannel();
    if (warehouse.value) {
      warehouses_id.value = warehouse.value.id;      
    }
    if (price_list.value) {
      price_lists_id.value = price_list.value.id;
    }
    if (sales_channel.value) {
      sales_channels_id.value = sales_channel.value.id;
    }
  await nextTick();
  if (!warehouses_id.value || !price_lists_id.value || !sales_channels_id.value) {
    expanded.value = true;
  }
  if (pos.selectedPosInvoice && pos.selectedPosInvoice.invoice) {
    if (warehouse.value) {
      pos.selectedPosInvoice.invoice.warehouses_id = warehouse.value.id;
      pos.selectedPosInvoice.invoice.warehouse = warehouse.value.name;
    }
    if (price_list.value) {
      pos.selectedPosInvoice.invoice.price_lists_id = price_list.value.id;
      pos.selectedPosInvoice.invoice.price_list = price_list.value.name;
    }
    if (sales_channel.value) {
      pos.selectedPosInvoice.invoice.sales_channels_id = sales_channel.value.id;
      pos.selectedPosInvoice.invoice.sales_channel = sales_channel.value.name;
      if (sales_channel.value.require_delivery) {
        pos.selectedPosInvoice.invoice.to_deliver = true;
      } 
    }
  }
});

</script>

<template>
  <div 
    class="bg-white border-b border-neutral-200"
    :class="{ 'shadow-sm': expanded }"
  >
    <div class="p-4 flex items-center justify-between">
      <div class="flex-1">
        <div v-if="!expanded && pos.selectedPosInvoice && pos.selectedPosInvoice.invoice.warehouses_id && pos.selectedPosInvoice.invoice.sales_channels_id && pos.selectedPosInvoice.invoice.price_lists_id">
          <p class="text-sm text-neutral-600">
            {{ pos.selectedPosInvoice.invoice.warehouse }} • {{ pos.selectedPosInvoice.invoice.price_list }} • {{ pos.selectedPosInvoice.invoice.sales_channel }}
          </p>
        </div>
        <button 
          v-else
          class="text-primary-500 text-sm font-medium"
          @click="expanded = !expanded"
        >
          Select Warehouse, Price List & Sales Channel
        </button>
      </div>
      
      <button 
        class="p-2 -m-2 text-neutral-400 hover:text-neutral-600"
        @click="expanded = !expanded"
      >
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          class="h-5 w-5" 
          :class="{ 'rotate-180': expanded }"
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
    </div>
    
    <div v-if="expanded" class="px-4 pb-4">
      <!-- Warehouse Selection -->
      <div class="mb-4">
        <SearchSelect
          id="warehouse"
          label="Warehouse"
          end-point="system/company/warehouses"
          v-model="warehouses_id"
          placeholder="Select warehouse"
          @select="selectWarehouse" />
      </div>

      <div class="mb-4">
        <SearchSelect
          id="price-list"
          label="Price List"
          end-point="finance/master-data/price-lists"
          v-model="price_lists_id"
          placeholder="Select Price List"
          @select="selectPriceList" />
      </div>
      
      <!-- Sales Channel Selection -->
      <div class="mb-4">
        <SearchSelect
          id="sales-channel"
          label="Sales Channel"
          end-point="revenue/master-data/sales-channels"
          v-model="sales_channels_id"
          placeholder="Select Sales Channel"
          @select="selectSalesChannel" />
      </div>
      
      <!-- Save as Default -->
      <div class="flex items-center">
        <input
          id="saveDefault"
          type="checkbox"
          @change="updateDefaults"
          v-model="saveAsDefault"
          class="h-4 w-4 text-primary-500 border-neutral-300 rounded"
        />
        <label for="saveDefault" class="ml-2 text-sm text-neutral-700">
          Save as default
        </label>
      </div>
    </div>
  </div>
</template>